import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, Check, ArrowRight } from "lucide-react";
import { useBilling } from "@/context/BillingContext";

const LABELS = {
  timeline: { name: "Timeline", copy: "Gantt scheduling with drag-to-reschedule and finish-to-start dependencies.", match: "Timeline" },
  mindmap: { name: "Mind map", copy: "Your private sticky-note board — capture ideas and turn them into real tasks.", match: "Mind map" },
  pto: { name: "Time Off", copy: "A shared time-off calendar with holidays, approvals and coverage warnings.", match: "Time Off" },
  reports: { name: "Reports", copy: "Client-ready printable reports that open with an AI executive summary, followed by progress, workload, overdue tasks and time off.", match: "reports" },
  audit: { name: "Audit log", copy: "Every change on the record, filterable and exportable to CSV.", match: "audit log" },
  review: { name: "To review", copy: "Send tasks to a named reviewer, approve or request changes straight from the queue, and track review cycles and time in review.", match: "Review" },
  announcements: { name: "Announcements", copy: "Pop-up updates for major news and key dates — critical ones stay pinned as a countdown banner, with seen-by tracking and one-click reminders.", match: "Announcements" },
};

export function UpgradeHint({ label, plan, testid, className = "" }) {
  return (
    <Link
      to="/app/billing"
      className={`flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 text-xs font-bold text-white/45 transition-colors duration-200 hover:border-[#00FF66]/40 hover:text-[#00FF66] ${className}`}
      data-testid={testid}
    >
      <Lock className="h-3.5 w-3.5" /> {label} · {plan} plan
    </Link>
  );
}

export default function PlanGate({ feature, children }) {
  const { sub, has, refresh } = useBilling();

  if (sub === null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="plan-gate-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#00FF66]" />
      </div>
    );
  }
  if (sub === false) {
    return (
      <div className="mx-auto max-w-md py-16 text-center" data-testid="plan-gate-error">
        <p className="text-sm text-white/50">We couldn't load your plan, so this page stays locked.</p>
        <button
          onClick={refresh}
          className="mt-5 rounded-full border border-white/15 px-5 py-2.5 text-sm font-bold text-white/70 transition-colors duration-200 hover:border-[#00FF66]/50 hover:text-[#00FF66]"
          data-testid="plan-gate-retry"
        >
          Try again
        </button>
      </div>
    );
  }
  if (has(feature)) return children;

  const info = LABELS[feature] || { name: feature, copy: "This feature is available on a higher plan." };
  const plans = sub.plans || [];
  const needed = plans.find((p) => (p.features || []).includes(feature));
  const from = plans.findIndex((p) => p.id === sub.plan);
  const unlocks = needed ? plans.slice(from + 1, plans.indexOf(needed) + 1) : [];
  const perksOf = (p) => (p.perks || []).filter((k) => !k.startsWith("Everything in"))
    .sort((a, b) => (info.match && b.includes(info.match) ? 1 : 0) - (info.match && a.includes(info.match) ? 1 : 0));

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="flex min-h-[calc(100vh-8rem)] items-center justify-center py-10"
      data-testid={`plan-gate-${feature}`}
    >
      <div className="relative w-full max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-10">
        <span className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#00FF66]/10 blur-3xl" aria-hidden="true" />
        <div className="mb-6 inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/40">
          <Lock className="h-5 w-5 text-[#00FF66]" />
        </div>
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[#00FF66]">Locked</p>
        <h1 className="font-display text-3xl font-black tracking-tighter sm:text-4xl">{info.name} is a {needed?.name || "paid"} feature.</h1>
        <p className="mt-4 max-w-lg text-base leading-relaxed text-white/50">{info.copy}</p>
        <p className="mt-2 text-sm text-white/40">
          You are on the <span className="font-semibold text-white/70">{sub.plan_name}</span> plan.
        </p>
        {unlocks.length > 0 && (
          <div className="mt-8 space-y-6 border-t border-white/[0.06] pt-7" data-testid="plan-gate-features">
            {unlocks.map((p) => (
              <div key={p.id} data-testid={`plan-gate-plan-${p.id}`}>
                <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">
                  {unlocks.length > 1 ? `Included with ${p.name}` : `Everything ${p.name} unlocks`}
                </p>
                <ul className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                  {perksOf(p).map((perk) => {
                    const hit = info.match && perk.includes(info.match);
                    return (
                      <li key={perk} className={`flex items-start gap-2.5 text-sm ${hit ? "font-semibold text-white" : "text-white/60"}`}>
                        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${hit ? "text-[#00FF66]" : "text-[#00FF66]/70"}`} />
                        {perk}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
        <Link
          to="/app/billing"
          className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#00FF66] px-6 py-3 text-sm font-bold text-black shadow-[0_0_30px_rgba(0,255,102,0.3)] transition-transform duration-200 hover:scale-[1.03]"
          data-testid="plan-gate-upgrade-button"
        >
          {needed ? `Upgrade to ${needed.name}` : "See plans"}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </motion.div>
  );
}
